import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "react-query";

import { BE_API } from "api/axios";
import useAuth from "hooks/useAuth";
import { AxiosError } from "axios";
import {IUserData, LoginData} from "types";

import { Logo } from "components/logo/logo";
import { LoginCard } from "pages/login/loginCard/loginCard";
import {IResponse} from "../../types/api";
import Footer from "../../components/footer/footer";
import {Stack} from "../../components/common";


const PageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	
    min-width: 100vw;
	height: 100dvh;
	
	overflow-y: auto;
    scrollbar-gutter: stable both-edges;
	background-color: ${props => props.theme.Background};

    background-image: url("/assets/loginBackground.png");
    background-repeat: repeat;
    background-size: cover;
    background-position-x: center;
    background-position-y: center;
    
	@supports (overflow-y: overlay) {
		overflow-y: overlay;
		//scrollbar-gutter: auto;
	}
`;

const Header = styled.div`
	color: ${props => props.theme.Gray5};
	font-weight: bold;
`;

const Text = styled.div`
	color: ${props => props.theme.Gray3};
    text-align: center;
    
    padding: 0px 60px;

    @media (max-width: 800px) {
        padding: 0px 10px;
        font-size: 12px;
    }
`;

const Text2 = styled.div`
	color: ${props => props.theme.TextDefault};
    text-align: center;
    
    padding: 0px 60px;

    @media (max-width: 800px) {
        padding: 0px 10px;
        font-size: 14px;
    }
`;

const MainContainer = styled.div`
    margin-top: 120px;
    padding-top: 20px;
    
    display: flex;
    flex-direction: column;
    gap: 30px;
    align-items: center;
    
    width: 100%;
    max-width: 600px;

    background-color: rgba(255, 255, 255, 0.95);

    color: ${props => props.theme.Gray5};

    @media (max-width: 800px) {
        gap: 15px;
        margin-top: 30px;
    }
`;


export const CredentialSignIn = async (payload: LoginData) => {

	const { data, headers: {authorization} } = await BE_API.post<IResponse<IUserData>>("/User/Authenticate", payload);
	return {respData: data, rememberMe: payload.rememberMe, token: authorization};
};

export const Login = () => {
	const { t } = useTranslation();
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();

	const { auth, setAuth } = useAuth();

	const SignInMutation = useMutation(CredentialSignIn);


	const getError = () => {
		if (SignInMutation.isError) {
			const error = SignInMutation.error as AxiosError;
			return error.response?.status.toString() || "500";
		}

		return null;
	};

	// On Credential SignIn
	useEffect(() => {
		if (!SignInMutation.isSuccess)
			return;

		if ( setAuth) {
			setAuth({succeeded: true, accessToken: SignInMutation.data.token, rememberMe: SignInMutation.data.rememberMe, userData: SignInMutation.data.respData.Data});
			//navigate(location.state as string || "/products");
		}

	}, [SignInMutation.isSuccess]);

	useEffect(() => {
		if(auth?.succeeded) {
			navigate(location.state as string || "/categories");
		}
	}, [auth]);

	const onLogin = (payload: LoginData) => {
		SignInMutation.mutate(payload);
	};

	return (
		<PageWrapper>
            <MainContainer>
                <Logo style={{ height: "60px", width: "unset" }} />
                <Header className="heading caps">Digital Library</Header>

                <Stack style={{alignItems: "center", gap: "15px"}}>
                    {<Text style={{lineHeight: "18px"}}>{t("LoginText1")}</Text>}
                    <Text2 style={{fontWeight: "bold"}}>{t("LoginText2")}</Text2>
                    <Text style={{fontStyle: "italic", paddingTop: "5px"}}>{t("LoginText3")}</Text>
                </Stack>

                <LoginCard
                    onSignIn={onLogin}
                    isLoading={SignInMutation.isLoading}
                    error={getError()}
                />
            </MainContainer>

            <Stack style={{flex: "1 0 auto", justifyContent: "flex-end", width: "100%"}}>
			    <Footer marginTop={"30px"}/>
            </Stack>
		</PageWrapper>
	);
};